import {GitHubAvatar} from '@github-ui/github-avatar'
import {goBack} from '@github-ui/history'
import type {IssueFormRef} from '@github-ui/issue-form/Types'
import {readRepositoryFromFragment, type RepositoryPickerRepository} from '@github-ui/item-picker/RepositoryPicker'
import {noop} from '@github-ui/noop'
import {userHovercardPath} from '@github-ui/paths'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {useNavigate, useSearchParams} from '@github-ui/use-navigate'
import {useUser} from '@github-ui/use-user'
import {Heading, Link} from '@primer/react'
import {useEffect, useMemo, useRef} from 'react'
import {useFragment} from 'react-relay'
import {graphql} from 'relay-runtime'

import type {IssueCreatePage$key} from './__generated__/IssueCreatePage.graphql'
import {LABELS} from './constants/labels'
import {useIssueCreateConfigContext} from './contexts/IssueCreateConfigContext'
import {IssueCreateContextProvider} from './contexts/IssueCreateContext'
import {useIssueCreateDataContext} from './contexts/IssueCreateDataContext'
import {useIssueCreateInitialValuesContext} from './contexts/IssueCreateInitialValuesContext'
import {CreateIssueFooter} from './CreateIssueFooter'
import {CreateIssueForm} from './CreateIssueForm'
import {DifferentTemplateLink} from './DifferentTemplateLink'
import {useOnCreateSuccess} from './hooks/use-on-create-success'
import {useSafeClose} from './hooks/use-safe-close'
import styles from './IssueCreatePage.module.css'
import type {SimilarIssue} from './SimilarIssues'
import {getSelectedTemplate, useHandleTemplateChange} from './use-handle-template-change'
import type {IssueCreatePayload} from './utils/model'
import {getSafeConfig, type IssueCreateOptionConfig, type SafeOptionConfig} from './utils/option-config'
import {getIssueCreateArguments, type IssueCreateMetadataTypes} from './utils/template-args'

export type IssueCreatePageProps = {
  initialMetadataValues: IssueCreateMetadataTypes
  currentRepository: IssueCreatePage$key
  storageKeyPrefix: string
  emojiSkinTonePreference: number | undefined
  copilotShowFunctionality: boolean
  /** Called when user clicks on a similar issue. If not provided, issues open in new tab. */
  onSimilarIssueClick?: (issue: SimilarIssue) => void
}

export const IssueCreatePage = ({
  initialMetadataValues,
  currentRepository,
  storageKeyPrefix,
  emojiSkinTonePreference,
  copilotShowFunctionality,
  onSimilarIssueClick,
}: IssueCreatePageProps) => {
  const data = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment IssueCreatePage on Repository
      @argumentDefinitions(
        templateFilter: {type: "String!", defaultValue: ""}
        withTemplate: {type: "Boolean!", defaultValue: false}
      ) {
        ...RepositoryPickerRepository
        name
        owner {
          login
        }
        hasAnyTemplates
        ...useHandleTemplateChange
          @dangerously_unaliased_fixme
          @arguments(filename: $templateFilter)
          @include(if: $withTemplate)
      }
    `,
    currentRepository,
  )
  const [urlSearchParams] = useSearchParams()
  const withTemplates = !!urlSearchParams.get('template')

  const repository = readRepositoryFromFragment(data)

  const {currentUser} = useUser()

  const initialContextValues = useIssueCreateInitialValuesContext()
  const issueCreateArguments = getIssueCreateArguments(urlSearchParams, initialMetadataValues, initialContextValues)

  const optionConfig: IssueCreateOptionConfig = {
    storageKeyPrefix,
    emojiSkinTonePreference,
    copilotShowFunctionality,
    issueCreateArguments: {
      ...issueCreateArguments,
      repository: {
        owner: data.owner.login,
        name: data.name,
      },
    },
    insidePortal: false,
  }
  const safeConfig = getSafeConfig(optionConfig)
  const template = withTemplates ? getSelectedTemplate(data) : undefined

  const preselectedData = {
    repository,
    template,
  }
  const issueFormRef = useRef<IssueFormRef>(null)

  const showUserRestrictedView = useMemo(() => {
    if (repository.viewerInteractionLimitReasonHTML) {
      return repository.viewerInteractionLimitReasonHTML.length > 0
    }
    return false
  }, [repository.viewerInteractionLimitReasonHTML])

  if (!currentUser) {
    reportError(
      new Error(`Could not find the current user when loading IssueCreatePage for ${ssrSafeLocation?.href.toString()}`),
    )
    return <div>Current user not found</div>
  }
  const {avatarUrl, login} = currentUser

  return (
    <IssueCreateContextProvider optionConfig={safeConfig} preselectedData={preselectedData}>
      <div className={styles.createPane}>
        <Link href={`/${login}`} className={styles.avatarLink}>
          <span className="sr-only">{LABELS.viewProfile(login)}</span>
          <GitHubAvatar
            src={avatarUrl}
            size={32}
            alt={''}
            data-hovercard-url={userHovercardPath({owner: login})}
            className={styles.avatar}
          />
        </Link>
        <div className={styles.createPaneContainer} data-testid="issue-create-pane-container">
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flexStart',
              marginBottom: template ? 4 : 12,
              position: 'relative',
              top: template ? '-10px' : 0,
            }}
          >
            <Heading as="h2" id="issue-create-pane-title" className={styles.IssueCreatePageHeading}>
              {LABELS.issueCreateDialogTitleTemplatePane()}
            </Heading>
            {data.hasAnyTemplates && !showUserRestrictedView && (
              <DifferentTemplateLink
                nameWithOwner={repository.nameWithOwner}
                templateName={template?.name}
                issueFormRef={issueFormRef}
              />
            )}
          </div>

          <div tabIndex={-1} className={styles.IssueCreatePageContainer}>
            <IssueCreatePageInternal
              template={template}
              repository={repository}
              optionConfig={safeConfig}
              issueFormRef={issueFormRef}
              showUserRestrictedView={showUserRestrictedView}
              onSimilarIssueClick={onSimilarIssueClick}
            />
          </div>
        </div>
      </div>
    </IssueCreateContextProvider>
  )
}

type IssueCreatePageInternalProps = {
  template: IssueCreatePayload | undefined
  repository: RepositoryPickerRepository
  issueFormRef: React.RefObject<IssueFormRef | null>
  showUserRestrictedView?: boolean
  optionConfig: SafeOptionConfig
  onSimilarIssueClick?: (issue: SimilarIssue) => void
}

const IssueCreatePageInternal = ({
  template,
  issueFormRef,
  repository,
  optionConfig,
  showUserRestrictedView,
  onSimilarIssueClick,
}: IssueCreatePageInternalProps) => {
  const defaultNavigate = useNavigate()
  const navigate = optionConfig.navigate || defaultNavigate
  const {setDisplayMode, setCreateMoreCreatedPath} = useIssueCreateConfigContext()
  const {title, setTitle, body, setBody, clearSessionData, usedStorageKeyPrefix} = useIssueCreateDataContext()

  const handleTemplateChange = useHandleTemplateChange({
    optionConfig,
    repository,
    navigate,
    setDisplayMode,
  })

  const onCreateSuccess = useOnCreateSuccess({
    issueFormRef,
    handleTemplateChange,
    navigate,
    template,
  })

  const {onSafeClose: onCancel} = useSafeClose({
    storageKeyPrefix: usedStorageKeyPrefix || '',
    issueFormRef,
    onCancel: () => {
      goBack()
    },
  })

  useEffect(() => {
    setCreateMoreCreatedPath({
      owner: repository.owner.login,
      repo: repository.name,
      number: undefined,
    })
  }, [repository, setCreateMoreCreatedPath])

  return (
    <CreateIssueForm
      issueFormRef={issueFormRef}
      onCreateSuccess={onCreateSuccess}
      onCreateError={noop}
      onCancel={onCancel}
      selectedTemplate={template}
      repository={repository}
      title={title}
      setTitle={setTitle}
      body={body}
      setBody={setBody}
      clearOnCreate={clearSessionData}
      focusTitleInput
      footer={<CreateIssueFooter onClose={onCancel} />}
      showUserRestrictedView={showUserRestrictedView}
      onSimilarIssueClick={onSimilarIssueClick}
    />
  )
}
